import { useEffect, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Check, ExternalLink, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { fmtMoney } from "@/lib/currency";

const CYCLES = [
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly · Save 20%" },
];

export default function BillingModal({ open, onOpenChange }) {
  const { subscription, refreshSubscription } = useAuth();
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null); // plan_id currently checking out

  useEffect(() => {
    if (!open) return;
    api.get("/plans/public").then((r) => setPlans(r.data || [])).catch(() => setPlans([]));
  }, [open]);

  const currentPlanId = subscription?.plan?.id;

  const subscribe = async (plan) => {
    setLoading(true);
    setPending(plan.id);
    try {
      const { data } = await api.post("/billing/subscribe", { plan_id: plan.id, cycle });
      if (data.short_url) {
        window.open(data.short_url, "_blank", "noopener,noreferrer");
        toast.success("Opened Razorpay checkout in a new tab. Finish payment there.");
      } else {
        toast.error("No checkout URL returned from Razorpay");
      }
      await refreshSubscription();
    } catch (err) {
      toast.error(fmtError(err.response?.data?.detail) || "Failed to start checkout");
    } finally {
      setLoading(false);
      setPending(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="billing-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl tracking-tighter flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            <Sparkles className="w-5 h-5" /> Choose your plan
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">Pick a plan below to renew or upgrade. We use Razorpay to process payments securely.</p>

        {/* Cycle toggle */}
        <div className="inline-flex border border-slate-300 rounded-sm overflow-hidden" data-testid="billing-cycle-toggle">
          {CYCLES.map((c) => (
            <button
              key={c.value}
              onClick={() => setCycle(c.value)}
              className={`px-4 py-2 text-xs uppercase tracking-widest ${cycle === c.value ? "bg-slate-950 text-white" : "text-slate-700 bg-white"}`}
              data-testid={`billing-cycle-${c.value}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-3 gap-3 mt-3">
          {plans.map((p) => {
            const isCurrent = p.id === currentPlanId;
            const disc = cycle === "yearly" ? (1 - (p.yearly_discount || 0)) : 1;
            const monthly = (p.base_price_monthly || 0) * disc;
            const yearly = monthly * 12;
            const cycleReady = p.is_contact_only || (cycle === "monthly" ? p.razorpay_plan_monthly : p.razorpay_plan_yearly);
            const isPro = p.slug === "professional";

            return (
              <div
                key={p.id}
                className={`border p-5 ${isPro ? "border-slate-950 bg-slate-950 text-white" : "border-slate-200 bg-white"}`}
                data-testid={`billing-card-${p.slug}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="font-bold text-lg" style={{ fontFamily: "Outfit, sans-serif" }}>{p.name}</div>
                  {isCurrent && <Badge variant="secondary" className="text-[9px] uppercase tracking-widest">Current</Badge>}
                </div>
                <p className={`text-xs mb-3 ${isPro ? "text-slate-300" : "text-slate-600"}`}>{p.tagline}</p>

                {p.is_contact_only ? (
                  <div className="mb-3">
                    <div className="text-2xl font-bold tracking-tighter" style={{ fontFamily: "Outfit, sans-serif" }}>Talk to us</div>
                  </div>
                ) : (
                  <div className="mb-3">
                    <div className="text-2xl font-bold tracking-tighter" style={{ fontFamily: "Outfit, sans-serif" }}>{fmtMoney(monthly, "INR")}<span className={`text-xs font-normal ${isPro ? "text-slate-400" : "text-slate-500"}`}>/mo</span></div>
                    <div className={`text-[11px] ${isPro ? "text-slate-400" : "text-slate-500"}`}>{p.base_employee_limit === 0 ? "Unlimited" : `Up to ${p.base_employee_limit}`} employees</div>
                    {cycle === "yearly" && <div className="text-[10px] mt-1 text-emerald-400">{fmtMoney(yearly, "INR")} billed yearly</div>}
                  </div>
                )}

                <ul className="space-y-1.5 mb-4 text-xs">
                  {Object.entries(p.modules || {}).filter(([, v]) => v).map(([k]) => (
                    <li key={k} className={`flex items-center gap-1.5 ${isPro ? "text-slate-200" : "text-slate-700"}`}>
                      <Check className="w-3 h-3 text-emerald-500" />
                      {k === "multi_outlet" ? "Multi-Outlet Tracking" : k === "tasks" ? "Task Management" : k.charAt(0).toUpperCase() + k.slice(1)}
                    </li>
                  ))}
                </ul>

                {p.is_contact_only ? (
                  <a href="mailto:info@hrbo.in?subject=HRBo%20Custom%20Plan" className="block">
                    <Button className="w-full rounded-sm" data-testid={`billing-contact-${p.slug}`}>Contact us</Button>
                  </a>
                ) : !cycleReady ? (
                  <Button disabled className="w-full rounded-sm" data-testid={`billing-disabled-${p.slug}`} title="Plan not synced to Razorpay yet">
                    Not available
                  </Button>
                ) : isCurrent ? (
                  <Button variant="outline" className="w-full rounded-sm" disabled data-testid={`billing-current-${p.slug}`}>You&apos;re on this plan</Button>
                ) : (
                  <Button
                    onClick={() => subscribe(p)}
                    disabled={loading}
                    className={`w-full rounded-sm ${isPro ? "bg-white text-slate-950 hover:bg-slate-100" : "bg-slate-950 hover:bg-slate-800"}`}
                    data-testid={`billing-subscribe-${p.slug}`}
                  >
                    {pending === p.id ? "Opening…" : <>Subscribe <ExternalLink className="w-3 h-3 ml-1" /></>}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-[11px] text-slate-500 mt-3">Payments are processed by Razorpay. Once payment succeeds, your workspace is automatically upgraded and the trial banner disappears — no need to refresh.</p>
      </DialogContent>
    </Dialog>
  );
}
